import { Outlet, useLocation } from 'react-router';
import { ToastProvider } from '@/components/ui/Toast';
import { ThemeProvider } from '@/lib/ThemeContext';
import { SessionProvider } from '@/contexts/SessionContext';
import { AgentProvider } from '@/contexts/AgentContext';
import { MyPageProvider } from '@/contexts/MyPageContext';
import AgentPanel, { AgentPanelProvider } from '@/components/agent/AgentPanel';
import RunDetailPanel, { RunDetailProvider } from '@/components/agent/RunDetailPanel';
import ReviewItemPanel, { ReviewPanelProvider } from '@/components/agent/review/ReviewItemPanel';
import { useAgentWebNotifications } from '@/lib/use-agent-web-notifications';
import { AnalyticsProvider } from '@/lib/analytics/AnalyticsProvider';

type Theme = 'light' | 'dark' | 'system';
type Density = 'comfortable' | 'compact';

function readCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? match[2] : null;
}

function readInitialTheme(): Theme {
    const v = readCookie('septima-theme');
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}

function readInitialDensity(): Density {
    const v = readCookie('septima-density');
    return v === 'compact' || v === 'comfortable' ? v : 'comfortable';
}

// Headless bridge: subscribes the in-app browser-notification dispatcher to the
// app-wide agent-jobs feed. Renders nothing; mounted inside AgentProvider so its
// useAgent()/useSessionQuery() reads resolve. Runs across every staff view.
function WebNotificationBridge() {
    useAgentWebNotifications();
    return null;
}

export function RootLayout() {
    const initialTheme = readInitialTheme();
    const initialDensity = readInitialDensity();
    // The client portal (/portal) is a client-facing surface with its own chrome
    // (PortalLayoutRoute) and no agent access (PRD #71) — the agent panel's
    // closed-state overlay would otherwise trip a WCAG aria-hidden check.
    const isPortal = useLocation().pathname.startsWith('/portal');

    return (
        <ToastProvider>
            <ThemeProvider initialTheme={initialTheme} initialDensity={initialDensity}>
                <SessionProvider>
                    <AgentProvider>
                        <WebNotificationBridge />
                        {/* Operational-analytics capture (issue #343) — staff only;
                            the portal is out of scope for Phase 1. */}
                        {!isPortal && <AnalyticsProvider />}
                        <RunDetailProvider>
                            <ReviewPanelProvider>
                                <AgentPanelProvider>
                                    <MyPageProvider>
                                        <div className="min-h-screen flex flex-col bg-[rgb(var(--color-page))] text-[rgb(var(--color-text-primary))]">
                                            <Outlet />
                                        </div>
                                        {!isPortal && <AgentPanel />}
                                        {!isPortal && <RunDetailPanel />}
                                        {!isPortal && <ReviewItemPanel />}
                                    </MyPageProvider>
                                </AgentPanelProvider>
                            </ReviewPanelProvider>
                        </RunDetailProvider>
                    </AgentProvider>
                </SessionProvider>
            </ThemeProvider>
        </ToastProvider>
    );
}
